import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Popup } from "@progress/kendo-react-popup";
import { isUserPartOfProject } from "../../../../components/TaskSidebar/sidebar.helper";
import { icon, label, notifyIcon, number } from "../../../../config";
import { usePopup } from "../../../../helper/commonHooks";
import { addProjectRequest, getAllProjects } from "../../../../shared/services/projects.service";
import { getNotification } from "../../../../utils/common";
import { prependString } from "../../../../utils/common";
import { calculateMemberCount, hasPendingJoinRequest } from "../../../../utils/joinRequest.helper";
import "./joinTeamPopup.scss";
import { Tooltip } from "@progress/kendo-react-tooltip";

/**
 * JoinTeamPopup component - Shows list of teams user is not part of
 * @author Muskan Thakur
 */
const JoinTeamPopup = ({ anchor, show, onClose, fromHeader }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const defaultDetails = useSelector((state) => state.tasks?.defaultDetails || {});
  const allProjectsList = defaultDetails?.allProjectsList || [];

  const allProjectsData = useSelector((state) => state.allProjects?.data || []);
  const { contentRef, blurTimeoutRef, onOpen, onFocus, onBlur } = usePopup();
  const [searchText, setSearchText] = useState("");

  /**
   * Get list of ProjectIds that have pending join requests

   */
  const pendingJoinRequestProjectIds = useMemo(() => {
    return new Set(
      allProjectsData
        .filter((project) => hasPendingJoinRequest(project))
        .map((project) => project?.ProjectId)
        .filter((id) => id !== null && id !== undefined)
    );
  }, [allProjectsData]);

  /**
   * Filter and format teams user is not part of - memoized for performance

   */
  const availableTeams = useMemo(() => {
    if (!show || !allProjectsList?.length || !defaultDetails) {
      return [];
    }

    /**
     * Filter projects where user is NOT a member AND does NOT have a pending join request
     */
    const available = allProjectsList.filter((project) => {
      const projectId = project?.value || project?.ProjectId;
      const isMember = isUserPartOfProject(defaultDetails, { value: projectId });

      const hasPendingRequest = pendingJoinRequestProjectIds.has(projectId);

      return (
        !isMember &&
        !hasPendingRequest &&
        (project?.label || project?.ProjectName) &&
        project?.isPersonal !== number.ONE &&
        project?.IsPersonal !== number.ONE &&
        projectId
      );
    });

    return available.map((project) => ({
      ProjectId: project?.value || project?.ProjectId,
      ProjectName: project?.label || project?.ProjectName || project?.name,
      ProjectCode: project?.ProjectCode || project?.code || project?.projectCode || "",
      members: calculateMemberCount(project),
    }));
  }, [show, allProjectsList, defaultDetails, pendingJoinRequestProjectIds]);

  const filteredTeams = useMemo(() => {
    if (!searchText) return availableTeams;

    const searchLower = searchText.toLowerCase();
    return availableTeams.filter(
      (team) => team?.ProjectName?.toLowerCase().includes(searchLower) || team?.ProjectCode?.toLowerCase().includes(searchLower)
    );
  }, [availableTeams, searchText]);

  /**
   * Handles join team request

   */
  const handleJoinTeam = useCallback(
    async (team) => {
      if (!team?.ProjectId || !user?.id) {
        return;
      }

      const response = await addProjectRequest({
        uId: user.id,
        projectId: team.ProjectId,
      });

      if (response?.data?.success) {
        getNotification(label.REQUEST_SENT, notifyIcon.SUCCESS_ICON);
        dispatch(getAllProjects());
        onClose?.();
      } else {
        getNotification(response?.data?.message, notifyIcon.ERROR_ICON);
      }
    },
    [user?.id, dispatch, onClose]
  );

  /**
   * Handles popup close
   */
  const handleClose = () => {
    setSearchText("");
    onClose && onClose();
  };

  /**
   * Handles blur event
   */
  const handleBlur = () => {
    onBlur(blurTimeoutRef, handleClose);
  };

  if (!show) return null;

  const teamName = user?.operationalTeamName || label.TEAM;

  return (
    <Popup
      anchor={anchor?.current}
      show={show}
      animate={false}
      anchorAlign={{ horizontal: "left", vertical: "top" }}
      popupAlign={{ horizontal: "left", vertical: "top" }}
      className={`${fromHeader ? "join-team-popup-container" : ""}`}
      popupClass={"dt-popup join-team-popup"}>
      <div className='join-team-popup-content' tabIndex={number.ZERO} onFocus={() => onFocus(blurTimeoutRef)} onBlur={handleBlur} ref={contentRef}>
        <div className='dt-popup-header d-flex justify-content-between align-items-center pt-2 pb-2 pl-3 pr-3'>
          <div className='dt-popup-title'>{prependString(teamName, label.JOIN)}</div>
          <span className="dt-popup-cancel-btn" onClick={handleClose} title={label.CLOSE_POPUP}>
            <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
              {icon.CLOSE}
            </Tooltip>
          </span>
        </div>
        <div className='dt-popup-body'>
          <div className='join-team-popup-search mb-2 border-bottom'>
            <div className='position-relative'>
              <input
                type='text'
                className='form-control'
                placeholder={label.SEARCH}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                autoFocus
              />
              <span className='position-absolute search-icon'>{icon.SEARCH}</span>
            </div>
          </div>
          <div className='join-team-popup-body overflow-x-hidden'>
            {filteredTeams.length > 0 ? (
              <div className='join-team-list'>
                {filteredTeams.map((team) => (
                  <div key={team?.ProjectId} className='join-team-item px-3 py-2 border-bottom'>
                    <div className='d-flex justify-content-between align-items-center'>
                      <div className='flex-grow-1 text-truncate'>
                        <div className='team-name-code d-flex align-items-center'>
                          <span className='font-weight-bold mr-2 text-truncate project-name'>{team?.ProjectName}</span>
                          {team?.ProjectCode && (
                            <span
                              className='team-code text-muted ml-2 mr-2 text-nowrap'
                              style={{ fontSize: "12px", padding: "2px 8px", backgroundColor: "#f3f4f6", borderRadius: "4px" }}>
                              {team?.ProjectCode}
                            </span>
                          )}
                        </div>
                        <div className='team-members-count text-muted small'>
                          {team?.members || 0} {team?.members === 1 ? "Member" : "Members"}
                        </div>
                      </div>
                      <button
                        className='btn btn-primary btn-sm join-team-btn'
                        onClick={() => handleJoinTeam(team)}>
                        {label.JOIN}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className='no-teams-placeholder p-4 text-center text-muted'>
                {searchText ? `No ${teamName.toLowerCase()}s found matching "${searchText}"` : `No ${teamName.toLowerCase()}s available to join`}
              </div>
            )}
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default JoinTeamPopup;
